import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
 
const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  
  // Mobile handled via CSS only
 
  // If user is logged in, redirect to dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);
 
  // Initialize landing page scripts after mount
  useEffect(() => {
    if (loading || user) return;
 
    // Aurora animation
    const canvas = document.getElementById('aurora');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      
      function resize() {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
      resize();
      window.addEventListener('resize', resize);
 
      const ribbons = [
        { color: [124, 58, 237], x: 0.3, y: 0.2, speed: 0.0003, amp: 120, freq: 0.8, phase: 0 },
        { color: [224, 64, 251], x: 0.6, y: 0.3, speed: 0.0004, amp: 100, freq: 1.0, phase: 1.5 },
        { color: [0, 229, 255],  x: 0.5, y: 0.15, speed: 0.00025, amp: 140, freq: 0.6, phase: 3 },
        { color: [168, 85, 247], x: 0.2, y: 0.4, speed: 0.00035, amp: 90, freq: 1.2, phase: 4.5 },
      ];
 
      let animationId;
      function draw() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ribbons.forEach(r => {
          r.phase += r.speed * 60;
          const cx = canvas.width * r.x + Math.sin(r.phase * 0.7) * 200;
          const cy = canvas.height * r.y + Math.cos(r.phase * 0.5) * 80;
          const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r.amp * 3);
          grad.addColorStop(0, `rgba(${r.color[0]},${r.color[1]},${r.color[2]},0.18)`);
          grad.addColorStop(0.4, `rgba(${r.color[0]},${r.color[1]},${r.color[2]},0.06)`);
          grad.addColorStop(1, `rgba(${r.color[0]},${r.color[1]},${r.color[2]},0)`);
          ctx.beginPath();
          ctx.ellipse(cx, cy, r.amp * 3, r.amp * 1.2, r.phase * 0.3, 0, Math.PI * 2);
          ctx.fillStyle = grad;
          ctx.fill();
        });
        animationId = requestAnimationFrame(draw);
      }
      draw();
 
      return () => {
        window.removeEventListener('resize', resize);
        if (animationId) cancelAnimationFrame(animationId);
      };
    }
  }, [loading, user]);
 
  // Initialize plexus animation
  useEffect(() => {
    if (loading || user) return;
 
    const plexus = document.getElementById('plexus');
    if (!plexus) return;
    const plx = plexus.getContext('2d');
    if (!plx) return;
    const heroEl = document.getElementById('hero');
 
    function resizePlexus() {
      plexus.width = Math.max(heroEl?.offsetWidth || window.innerWidth, window.innerWidth);
      plexus.height = Math.max(heroEl?.offsetHeight || window.innerHeight, window.innerHeight);
    }
 
    const COLORS = [[124,58,237],[224,64,251],[0,229,255],[168,85,247],[34,211,238]];
    const isMobileDevice = window.innerWidth <= 768;
    const NODE_COUNT = isMobileDevice ? 45 : 70;
    const CONN_DIST = isMobileDevice ? 100 : 180;
    const ASSEMBLE_FRAMES = 380;
    const LINE_FADE_START = 280;
    let rotY = 0, rotX = 0.3;
    let nodes = [];
    let fc = 0;
    let done = false;
    let animationId;
 
    function getR() { return Math.min(plexus.width, plexus.height) * 0.28; }
    function getCX() { return plexus.width / 2; }
    function getCY() { return plexus.height * 0.38; }
    function easeOut(t) { return 1 - Math.pow(1-t, 3); }
 
    function buildNodes() {
      nodes = [];
      for (let i = 0; i < NODE_COUNT; i++) {
        const phi = Math.acos(1 - 2*(i+0.5)/NODE_COUNT);
        const theta = Math.PI*(1+Math.sqrt(5))*i;
        const scatter = 300 + Math.random()*350;
        const sa = Math.random()*Math.PI*2;
        nodes.push({
          phi, theta,
          color: COLORS[i % COLORS.length],
          size: 1 + Math.random()*1.5,
          pulse: Math.random()*Math.PI*2,
          ps: 0.02 + Math.random()*0.02,
          bright: 0.6 + Math.random()*0.4,
          sx: getCX() + Math.cos(sa)*scatter,
          sy: getCY() + Math.sin(sa)*scatter,
          delay: Math.floor(Math.random()*150),
        });
      }
    }
 
    function getTarget(n) {
      const r = getR();
      let x = r*Math.sin(n.phi)*Math.cos(n.theta);
      let y = r*Math.cos(n.phi);
      let z = r*Math.sin(n.phi)*Math.sin(n.theta);
      const cY=Math.cos(rotY),sY=Math.sin(rotY);
      const x1=x*cY-z*sY, z1=x*sY+z*cY;
      const cX=Math.cos(rotX),sX=Math.sin(rotX);
      const y2=y*cX-z1*sX, z2=y*sX+z1*cX;
      const sc=800/(800+z2);
      return { x:getCX()+x1*sc, y:getCY()+y2*sc, sc, z:z2, rx:x1, ry:y2, rz:z2 };
    }
 
    function frame() {
      try {
        plx.clearRect(0,0,plexus.width,plexus.height);
        if (done) { rotY+=0.003; rotX+=0.0007; }
 
        const pts = nodes.map(n => {
          n.pulse += n.ps;
          const t = getTarget(n);
          let px, py;
          if (done) {
            px = t.x; py = t.y;
          } else {
            const raw = (fc - n.delay) / (ASSEMBLE_FRAMES - n.delay);
            const p = easeOut(Math.max(0, Math.min(1, raw)));
            px = n.sx + (t.x - n.sx)*p;
            py = n.sy + (t.y - n.sy)*p;
          }
          return { x:px, y:py, sc:t.sc, z:t.z, rx:t.rx, ry:t.ry, rz:t.rz, n };
        });
 
        pts.sort((a,b)=>a.z-b.z);
 
        const lineMul = done ? 1 : Math.max(0, Math.min(1, (fc-LINE_FADE_START)/50));
        if (lineMul > 0) {
          for (let i=0;i<pts.length;i++) {
            for (let j=i+1;j<pts.length;j++) {
              const a=pts[i], b=pts[j];
              const d=Math.sqrt((a.rx-b.rx)**2+(a.ry-b.ry)**2+(a.rz-b.rz)**2);
              if (d<CONN_DIST) {
                const alpha=(1-d/CONN_DIST)*0.6*(a.sc+b.sc)/2*lineMul;
                const c=a.n.color;
                plx.beginPath(); plx.moveTo(a.x,a.y); plx.lineTo(b.x,b.y);
                plx.strokeStyle=`rgba(${c[0]},${c[1]},${c[2]},${alpha})`;
                plx.lineWidth=0.8; plx.stroke();
              }
            }
          }
        }
 
        pts.forEach(p => {
          const n=p.n;
          const raw = (fc - n.delay) / (ASSEMBLE_FRAMES*0.5 - n.delay);
          const fadeIn = done ? 1 : Math.max(0, Math.min(1, raw));
          const alpha = Math.min(n.bright*p.sc*(1+Math.sin(n.pulse)*0.4)*fadeIn, 1);
          const sz = n.size*p.sc*3.5;
          const c = n.color;
          const g = plx.createRadialGradient(p.x,p.y,0,p.x,p.y,sz*4);
          g.addColorStop(0,`rgba(${c[0]},${c[1]},${c[2]},${alpha})`);
          g.addColorStop(0.4,`rgba(${c[0]},${c[1]},${c[2]},${alpha*0.3})`);
          g.addColorStop(1,`rgba(${c[0]},${c[1]},${c[2]},0)`);
          plx.beginPath(); plx.arc(p.x,p.y,sz*4,0,Math.PI*2);
          plx.fillStyle=g; plx.fill();
          plx.beginPath(); plx.arc(p.x,p.y,Math.max(sz*0.7,1),0,Math.PI*2);
          plx.fillStyle=`rgba(${c[0]},${c[1]},${c[2]},${Math.min(alpha*2,1)})`;
          plx.fill();
        });
 
        fc++;
        if (!done && fc > ASSEMBLE_FRAMES + 30) done = true;
      } catch(e) {}
      animationId = requestAnimationFrame(frame);
    }
 
    resizePlexus();
    buildNodes();
    
    const handleResize = () => { resizePlexus(); buildNodes(); fc=0; done=false; };
    window.addEventListener('resize', handleResize);
    frame();
 
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [loading, user]);
 
  // Initialize scroll reveal
  useEffect(() => {
    if (loading || user) return;
 
    const reveals = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(entries => {
      entries.forEach((e, i) => {
        if (e.isIntersecting) {
          setTimeout(() => e.target.classList.add('visible'), i * 80);
        }
      });
    }, { threshold: 0.1 });
    reveals.forEach(r => observer.observe(r));
 
    return () => observer.disconnect();
  }, [loading, user]);
 
  // Handle navigation clicks
  const handleGetStarted = (e) => {
    e.preventDefault();
    navigate('/login');
  };
 
  const handleSignIn = (e) => {
    e.preventDefault();
    navigate('/login');
  };
 
  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
 
  // FAQ toggle
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };
 
  // Mobile menu toggle
  const toggleMobileMenu = () => {
    const hamburger = document.getElementById('hamburger');
    const mobileMenu = document.getElementById('mobileMenu');
    hamburger?.classList.toggle('active');
    mobileMenu?.classList.toggle('open');
  };
 
  const closeMobileMenu = () => {
    const hamburger = document.getElementById('hamburger');
    const mobileMenu = document.getElementById('mobileMenu');
    hamburger?.classList.remove('active');
    mobileMenu?.classList.remove('open');
  };
 
  // Billing toggle
  const setBilling = (billing) => {
    document.querySelectorAll('.toggle-option').forEach(btn => {
      if (btn.dataset.billing === billing) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
    
    document.querySelectorAll('.pricing-card').forEach(card => {
      const priceEl = card.querySelector('.price-amount');
      const descDefault = card.querySelector('.plan-desc-default');
      const descYearly = card.querySelector('.plan-desc-yearly');
      
      if (priceEl) {
        const monthlyPrice = priceEl.dataset.monthly;
        const yearlyPrice = priceEl.dataset.yearly;
        priceEl.textContent = billing === 'yearly' ? yearlyPrice : monthlyPrice;
      }
      
      if (descDefault && descYearly) {
        if (billing === 'yearly') {
          descDefault.style.display = 'none';
          descYearly.style.display = 'block';
        } else {
          descDefault.style.display = 'block';
          descYearly.style.display = 'none';
        }
      }
    });
  };
 
  // Show loading while checking auth
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#080c1a', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '2px solid #7c3aed',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
 
  // Don't render if user is logged in (will redirect)
  if (user) {
    return null;
  }
 
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cabinet+Grotesk:wght@400;500;700;800;900&family=Satoshi:wght@300;400;500;700&display=swap');
        @import url('https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@800,700,500,400&f[]=satoshi@700,500,400,300&display=swap');
 
        :root {
          --navy: #080c1a;
          --navy2: #0d1328;
          --purple: #7c3aed;
          --purple2: #a855f7;
          --magenta: #e040fb;
          --cyan: #00e5ff;
          --cyan2: #22d3ee;
          --white: #f0f4ff;
          --muted: #8892b0;
        }
 
        .landing-page * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
 
        .landing-page {
          background: var(--navy);
          color: var(--white);
          font-family: 'Satoshi', 'DM Sans', sans-serif;
          overflow-x: hidden;
          min-height: 100vh;
        }
 
        #aurora {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          z-index: 0;
          pointer-events: none;
        }
 
        .landing-page::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 1;
          opacity: 0.4;
        }
 
        #plexus {
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          z-index: 0;
          pointer-events: none;
        }
 
        #hero > *:not(#plexus) {
          position: relative;
          z-index: 2;
        }
 
        .landing-nav {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 100;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.4rem 4rem;
          background: rgba(8,12,26,0.6);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(124,58,237,0.15);
        }
 
        .logo {
          font-family: 'Cabinet Grotesk', 'Syne', sans-serif;
          font-weight: 800;
          font-size: 1.4rem;
          background: linear-gradient(90deg, var(--cyan), var(--magenta));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: -0.5px;
        }
 
        .nav-links {
          display: flex;
          gap: 2.5rem;
          list-style: none;
        }
 
        .nav-links a {
          color: var(--muted);
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 500;
          transition: color 0.3s;
          cursor: pointer;
        }
 
        .nav-links a:hover { color: var(--white); }
 
        .nav-cta {
          background: linear-gradient(135deg, var(--purple), var(--magenta));
          color: white;
          border: none;
          padding: 0.6rem 1.5rem;
          border-radius: 100px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 500;
          font-size: 0.9rem;
          cursor: pointer;
          transition: opacity 0.3s, transform 0.3s;
        }
 
        .nav-cta:hover { opacity: 0.85; transform: scale(1.04); }
 
        .hamburger {
          display: none;
          flex-direction: column;
          justify-content: center;
          gap: 5px;
          width: 28px;
          height: 28px;
          cursor: pointer;
          z-index: 102;
        }
 
        .hamburger span {
          display: block;
          width: 100%;
          height: 2px;
          background: var(--white);
          border-radius: 2px;
          transition: all 0.3s ease;
        }
 
        .hamburger.active span:nth-child(1) { transform: rotate(45deg) translate(5px, 5px); }
        .hamburger.active span:nth-child(2) { opacity: 0; }
        .hamburger.active span:nth-child(3) { transform: rotate(-45deg) translate(5px, -5px); }
 
        .mobile-menu {
          display: none;
          position: fixed;
          top: 70px;
          left: 0;
          right: 0;
          background: rgba(8, 12, 26, 0.98);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(124, 58, 237, 0.2);
          padding: 1.5rem 2rem 2rem;
          z-index: 99;
          flex-direction: column;
          gap: 0.5rem;
          opacity: 0;
          transform: translateY(-10px);
          transition: opacity 0.3s ease, transform 0.3s ease;
          pointer-events: none;
        }
 
        .mobile-menu.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
 
        .mobile-menu a {
          display: block;
          color: var(--muted);
          text-decoration: none;
          font-size: 1rem;
          font-weight: 500;
          padding: 0.9rem 0;
          border-bottom: 1px solid rgba(124, 58, 237, 0.1);
          transition: color 0.3s, padding-left 0.3s;
        }
 
        .mobile-menu a:hover { color: var(--white); padding-left: 0.5rem; }
        .mobile-menu a:last-of-type { border-bottom: none; }
 
        .mobile-menu .mobile-cta {
          margin-top: 1rem;
          background: linear-gradient(135deg, var(--purple), var(--magenta));
          color: white;
          border: none;
          padding: 0.9rem 1.5rem;
          border-radius: 100px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          text-align: center;
          text-decoration: none;
          display: block;
          transition: opacity 0.3s, transform 0.3s;
          box-shadow: 0 0 20px rgba(224, 64, 251, 0.3);
        }
 
        .mobile-menu .mobile-cta:hover { opacity: 0.9; transform: scale(1.02); }
 
        #hero {
          position: relative;
          z-index: 2;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 8rem 2rem 4rem;
        }
 
        .badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(124,58,237,0.15);
          border: 1px solid rgba(124,58,237,0.4);
          border-radius: 100px;
          padding: 0.35rem 1rem;
          font-size: 0.78rem;
          color: var(--cyan2);
          font-weight: 500;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          margin-bottom: 2rem;
          animation: fadeUp 0.8s ease both;
        }
 
        .badge-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--cyan);
          animation: pulse 2s infinite;
        }
 
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.8); }
        }
 
        .landing-page h1 {
          font-family: 'Cabinet Grotesk', 'Syne', sans-serif;
          font-weight: 800;
          font-size: clamp(3rem, 7vw, 6.5rem);
          line-height: 1.0;
          letter-spacing: -2px;
          margin-bottom: 1.5rem;
          animation: fadeUp 0.8s 0.1s ease both;
        }
 
        .gradient-text {
          background: linear-gradient(135deg, var(--cyan) 0%, var(--purple2) 40%, var(--magenta) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
 
        .hero-sub {
          font-size: clamp(1rem, 2vw, 1.25rem);
          color: var(--muted);
          max-width: 560px;
          line-height: 1.7;
          margin-bottom: 3rem;
          font-weight: 300;
          animation: fadeUp 0.8s 0.2s ease both;
        }
 
        .hero-sub span { color: var(--white); font-weight: 500; }
 
        .hero-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
          flex-wrap: wrap;
          justify-content: center;
          animation: fadeUp 0.8s 0.3s ease both;
        }
 
        .btn-primary {
          position: relative;
          background: linear-gradient(135deg, var(--purple), var(--magenta));
          color: white;
          border: none;
          padding: 0.9rem 2.2rem;
          border-radius: 100px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: transform 0.3s, box-shadow 0.3s;
          box-shadow: 0 0 30px rgba(224,64,251,0.3);
          text-decoration: none;
          display: inline-block;
        }
 
        .btn-primary:hover {
          transform: translateY(-2px) scale(1.03);
          box-shadow: 0 0 50px rgba(224,64,251,0.5);
        }
 
        .btn-ghost {
          color: var(--muted);
          background: transparent;
          border: 1px solid rgba(136,146,176,0.3);
          padding: 0.9rem 2.2rem;
          border-radius: 100px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 500;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s;
          text-decoration: none;
          display: inline-block;
        }
 
        .btn-ghost:hover { border-color: var(--cyan); color: var(--cyan); }
 
        .hero-visual {
          margin-top: 8rem;
          position: relative;
          width: 100%;
          max-width: 900px;
          animation: fadeUp 0.8s 0.5s ease both;
        }
 
        .dashboard-mock {
          background: rgba(13,19,40,0.8);
          border: 1px solid rgba(124,58,237,0.25);
          border-radius: 20px;
          padding: 2rem;
          backdrop-filter: blur(20px);
          box-shadow: 0 40px 100px rgba(0,0,0,0.5), 0 0 60px rgba(124,58,237,0.1);
          position: relative;
          overflow: hidden;
        }
 
        .dashboard-mock::before {
          content: '';
          position: absolute;
          top: -1px; left: 20%; right: 20%;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--cyan), var(--magenta), transparent);
        }
 
        .mock-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }
 
        .mock-dot { width: 10px; height: 10px; border-radius: 50%; }
        .mock-dot:nth-child(1) { background: #ff5f56; }
        .mock-dot:nth-child(2) { background: #ffbd2e; }
        .mock-dot:nth-child(3) { background: #27c93f; }
 
        .mock-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 1rem;
        }
 
        .mock-card {
          background: rgba(124,58,237,0.08);
          border: 1px solid rgba(124,58,237,0.2);
          border-radius: 12px;
          padding: 1.2rem;
          transition: border-color 0.3s;
        }
 
        .mock-card:hover { border-color: rgba(224,64,251,0.4); }
 
        .mock-card-label {
          font-size: 0.7rem;
          color: var(--muted);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 0.5rem;
        }
 
        .mock-card-value {
          font-family: 'Syne', sans-serif;
          font-size: 1.8rem;
          font-weight: 700;
        }
 
        .mock-card-value.c1 { color: var(--cyan); }
        .mock-card-value.c2 { color: var(--magenta); }
        .mock-card-value.c3 { color: var(--purple2); }
 
        .mock-bar-section { margin-top: 1rem; }
 
        .mock-bar-label {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: var(--muted);
          margin-bottom: 0.4rem;
        }
 
        .mock-bar {
          height: 6px;
          border-radius: 100px;
          background: rgba(255,255,255,0.05);
          overflow: hidden;
          margin-bottom: 0.6rem;
        }
 
        .mock-bar-fill {
          height: 100%;
          border-radius: 100px;
          animation: barGrow 1.5s ease both;
        }
 
        @keyframes barGrow { from { width: 0; } }
 
        #features, #pricing { scroll-margin-top: 80px; }
 
        #features {
          position: relative;
          z-index: 2;
          padding: 8rem 4rem;
          max-width: 1200px;
          margin: 0 auto;
        }
 
        .section-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: var(--cyan);
          font-weight: 600;
          margin-bottom: 1rem;
          text-align: center;
        }
 
        .section-title {
          font-family: 'Cabinet Grotesk', 'Syne', sans-serif;
          font-size: clamp(2rem, 4vw, 3.2rem);
          font-weight: 800;
          text-align: center;
          letter-spacing: -1px;
          margin-bottom: 1rem;
          line-height: 1.1;
        }
 
        .section-sub {
          text-align: center;
          color: var(--muted);
          font-size: 1.05rem;
          max-width: 500px;
          margin: 0 auto 4rem;
          line-height: 1.7;
        }
 
        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }
        
        .features-grid.features-grid-mobile {
          grid-template-columns: 1fr !important;
          gap: 1rem !important;
        }
        
        .feature-card.feature-card-mobile {
          grid-column: span 1 !important;
          flex-direction: column !important;
        }
 
        .feature-card {
          background: rgba(13,19,40,0.7);
          border: 1px solid rgba(124,58,237,0.15);
          border-radius: 20px;
          padding: 2rem;
          position: relative;
          overflow: hidden;
          transition: transform 0.3s, border-color 0.3s, box-shadow 0.3s;
          cursor: default;
        }
 
        .feature-card:hover {
          transform: translateY(-6px);
          border-color: rgba(124,58,237,0.4);
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
 
        .feature-card.big {
          grid-column: span 2;
          display: flex;
          gap: 2rem;
          align-items: center;
        }
 
        .feature-card.big .feature-content { flex: 1; }
 
        .feature-card.big .feature-visual {
          flex: 1;
          background: rgba(124,58,237,0.06);
          border-radius: 12px;
          padding: 1.2rem;
          border: 1px solid rgba(124,58,237,0.15);
        }
 
        .feature-icon {
          width: 48px; height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
          margin-bottom: 1.2rem;
        }
 
        .fi-cyan { background: rgba(0,229,255,0.12); }
        .fi-purple { background: rgba(168,85,247,0.12); }
        .fi-magenta { background: rgba(224,64,251,0.12); }
 
        .feature-title {
          font-family: 'Cabinet Grotesk', 'Syne', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          margin-bottom: 0.6rem;
          letter-spacing: -0.3px;
        }
 
        .feature-desc {
          color: var(--muted);
          font-size: 0.9rem;
          line-height: 1.7;
        }
 
        .divider {
          position: relative;
          z-index: 2;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(124,58,237,0.3), rgba(0,229,255,0.3), transparent);
          margin: 0 4rem;
        }
 
        #pricing {
          position: relative;
          z-index: 2;
          padding: 8rem 4rem;
          max-width: 1100px;
          margin: 0 auto;
        }
 
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          margin-top: 1rem;
        }
 
        .billing-toggle {
          display: flex;
          justify-content: center;
          gap: 0;
          margin-bottom: 2.5rem;
          background: rgba(13,19,40,0.8);
          border: 1px solid rgba(124,58,237,0.2);
          border-radius: 100px;
          padding: 4px;
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }
 
        .toggle-option {
          position: relative;
          padding: 0.7rem 1.8rem;
          border-radius: 100px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          color: var(--muted);
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
 
        .toggle-option:hover { color: var(--white); }
 
        .toggle-option.active {
          background: linear-gradient(135deg, var(--purple), var(--purple2));
          color: white;
          box-shadow: 0 0 20px rgba(124,58,237,0.4);
        }
 
        .save-badge {
          background: #22c55e;
          color: white;
          font-size: 0.65rem;
          font-weight: 700;
          padding: 0.2rem 0.5rem;
          border-radius: 100px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
 
        .toggle-option.active .save-badge { background: rgba(255,255,255,0.25); }
 
        .pricing-card {
          background: rgba(13,19,40,0.7);
          border: 1px solid rgba(124,58,237,0.15);
          border-radius: 24px;
          padding: 2.5rem 2rem;
          position: relative;
          transition: transform 0.3s, box-shadow 0.3s;
        }
 
        .pricing-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 30px 80px rgba(0,0,0,0.4);
        }
 
        .pricing-card.featured {
          border-color: rgba(168,85,247,0.5);
          background: rgba(124,58,237,0.08);
          box-shadow: 0 0 60px rgba(124,58,237,0.12);
        }
 
        .pricing-card.featured::before {
          content: '';
          position: absolute;
          top: -1px; left: 15%; right: 15%;
          height: 2px;
          background: linear-gradient(90deg, var(--purple), var(--purple2));
          border-radius: 100px;
        }
 
        .popular-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, var(--purple), var(--purple2));
          color: white;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          padding: 0.25rem 1rem;
          border-radius: 100px;
          white-space: nowrap;
        }
 
        .plan-name {
          font-family: 'Cabinet Grotesk', 'Syne', sans-serif;
          font-size: 1rem;
          font-weight: 700;
          color: var(--muted);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 1.5rem;
        }
 
        .plan-price {
          display: flex;
          align-items: flex-end;
          gap: 0.3rem;
          margin-bottom: 0.5rem;
        }
 
        .price-amount {
          font-family: 'Cabinet Grotesk', 'Syne', sans-serif;
          font-size: 3.5rem;
          font-weight: 800;
          line-height: 1;
          letter-spacing: -2px;
        }
 
        .price-period { color: var(--muted); font-size: 0.9rem; padding-bottom: 0.5rem; }
 
        .plan-desc {
          color: var(--muted);
          font-size: 0.85rem;
          margin-bottom: 2rem;
          line-height: 1.6;
        }
 
        .plan-features {
          list-style: none;
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
        }
 
        .plan-features li {
          display: flex;
          align-items: center;
          gap: 0.7rem;
          font-size: 0.88rem;
          color: var(--white);
        }
 
        .check {
          width: 18px; height: 18px;
          border-radius: 50%;
          background: rgba(0,229,255,0.12);
          border: 1px solid rgba(0,229,255,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 0.6rem;
          color: var(--cyan);
        }
 
        .plan-btn {
          width: 100%;
          padding: 0.9rem;
          border-radius: 100px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s;
          border: none;
        }
 
        .plan-btn.outline {
          background: transparent;
          border: 1px solid rgba(124,58,237,0.4);
          color: var(--white);
        }
 
        .plan-btn.outline:hover {
          border-color: var(--purple2);
          background: rgba(124,58,237,0.1);
        }
 
        .plan-btn.solid {
          background: linear-gradient(135deg, var(--purple), var(--magenta));
          color: white;
          box-shadow: 0 0 30px rgba(224,64,251,0.3);
        }
 
        .plan-btn.solid:hover {
          transform: scale(1.02);
          box-shadow: 0 0 50px rgba(224,64,251,0.5);
        }
 
        .landing-footer {
          position: relative;
          z-index: 2;
          text-align: center;
          padding: 4rem 2rem;
          border-top: 1px solid rgba(124,58,237,0.1);
          color: var(--muted);
          font-size: 0.85rem;
        }
 
        .landing-footer .logo { display: inline-block; margin-bottom: 1rem; font-size: 1.2rem; }
 
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
 
        .reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
 
        .reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }
 
        .workflow-container {
          display: flex;
          align-items: flex-start;
          gap: 0;
          margin-top: 3rem;
          overflow-x: auto;
          padding-bottom: 1rem;
        }
 
        .workflow-step {
          flex: 1;
          min-width: 150px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
 
        .workflow-icon {
          width: 72px;
          height: 72px;
          border-radius: 20px;
          border: 1px solid;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.8rem;
          margin-bottom: 1.2rem;
          transition: transform 0.3s, box-shadow 0.3s;
          backdrop-filter: blur(10px);
        }
 
        .workflow-step:hover .workflow-icon {
          transform: translateY(-6px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
 
        .workflow-num {
          font-family: 'Cabinet Grotesk', 'Syne', sans-serif;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.1em;
          margin-bottom: 0.4rem;
          opacity: 0.7;
        }
 
        .workflow-title {
          font-family: 'Cabinet Grotesk', 'Syne', sans-serif;
          font-size: 1.1rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
        }
 
        .workflow-desc {
          color: var(--muted);
          font-size: 0.82rem;
          line-height: 1.6;
          max-width: 140px;
          margin: 0 auto;
        }
 
        .workflow-arrow {
          font-size: 1.4rem;
          color: rgba(136,146,176,0.3);
          padding: 0 0.5rem;
          margin-top: 1.2rem;
          flex-shrink: 0;
          animation: arrowPulse 2s infinite;
        }
 
        @keyframes arrowPulse {
          0%, 100% { opacity: 0.3; transform: translateX(0); }
          50% { opacity: 0.8; transform: translateX(4px); }
        }
 
        .workflow-publish {
          margin-top: 3rem;
          background: rgba(13,19,40,0.8);
          border: 1px solid rgba(124,58,237,0.2);
          border-radius: 16px;
          padding: 1.5rem 2rem;
          backdrop-filter: blur(20px);
          position: relative;
          overflow: hidden;
        }
 
        .workflow-publish::before {
          content: '';
          position: absolute;
          top: -1px; left: 10%; right: 10%;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--cyan), var(--magenta), transparent);
        }
 
        .workflow-publish-inner {
          display: flex;
          align-items: center;
          gap: 1.2rem;
          flex-wrap: wrap;
        }
 
        .platform-tag {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.3rem 0.8rem;
          border-radius: 100px;
          border: 1px solid;
        }
 
        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          margin-top: 1rem;
        }
 
        .testi-card {
          background: rgba(13,19,40,0.7);
          border: 1px solid rgba(124,58,237,0.15);
          border-radius: 20px;
          padding: 2rem;
          transition: transform 0.3s, border-color 0.3s;
          position: relative;
          overflow: hidden;
        }
 
        .testi-card:hover {
          transform: translateY(-4px);
          border-color: rgba(124,58,237,0.35);
        }
 
        .testi-card.featured-testi {
          border-color: rgba(168,85,247,0.35);
          background: rgba(124,58,237,0.07);
        }
 
        .testi-stars {
          color: #fbbf24;
          font-size: 0.85rem;
          letter-spacing: 2px;
          margin-bottom: 1rem;
        }
 
        .testi-text {
          color: var(--white);
          font-size: 0.92rem;
          line-height: 1.75;
          margin-bottom: 1.5rem;
          font-style: italic;
          opacity: 0.9;
        }
 
        .testi-author {
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }
 
        .testi-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Cabinet Grotesk', sans-serif;
          font-weight: 800;
          font-size: 1rem;
          color: white;
          flex-shrink: 0;
        }
 
        .testi-name {
          font-family: 'Cabinet Grotesk', sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
        }
 
        .testi-role {
          color: var(--muted);
          font-size: 0.78rem;
          margin-top: 0.1rem;
        }
 
        .faq-list {
          margin-top: 3rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
 
        .faq-item {
          background: rgba(13,19,40,0.7);
          border: 1px solid rgba(124,58,237,0.15);
          border-radius: 14px;
          overflow: hidden;
          transition: border-color 0.3s;
        }
 
        .faq-item:hover { border-color: rgba(124,58,237,0.35); }
 
        .faq-q {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.3rem 1.5rem;
          cursor: pointer;
          font-family: 'Cabinet Grotesk', sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          gap: 1rem;
          user-select: none;
        }
 
        .faq-icon {
          color: var(--purple2);
          font-size: 1.4rem;
          font-weight: 300;
          flex-shrink: 0;
          transition: transform 0.3s;
          line-height: 1;
        }
 
        .faq-a {
          max-height: 0;
          overflow: hidden;
          padding: 0 1.5rem;
          color: var(--muted);
          font-size: 0.9rem;
          line-height: 1.7;
          transition: max-height 0.4s ease, padding 0.3s ease;
        }
 
        .faq-item.open .faq-a {
          max-height: 200px;
          padding: 0 1.5rem 1.3rem;
        }
 
        .faq-item.open .faq-icon { transform: rotate(45deg); color: var(--magenta); }
        .faq-item.open { border-color: rgba(168,85,247,0.35); }
 
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: var(--navy); }
        ::-webkit-scrollbar-thumb { background: rgba(124,58,237,0.4); border-radius: 3px; }
 
        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .landing-nav { padding: 1rem 1.5rem; }
          .nav-links { display: none; }
          .nav-cta { display: none; }
          .hamburger { display: flex; }
          .mobile-menu { display: flex; }
 
          #hero { 
            padding: 6rem 1.5rem 3rem;
            min-height: auto;
          }
 
 
 
          .landing-page h1 {
            font-size: 2.5rem;
            letter-spacing: -1px;
          }
 
          .hero-sub {
            font-size: 1rem;
            margin-bottom: 2rem;
          }
 
          .hero-actions {
            flex-direction: column;
            width: 100%;
            gap: 0.8rem;
          }
 
          .btn-primary, .btn-ghost {
            width: 100%;
            text-align: center;
            padding: 1rem 1.5rem;
          }
 
          .hero-visual {
            margin-top: 4rem;
          }
 
          .dashboard-mock {
            padding: 1.2rem;
          }
 
          .mock-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 0.6rem;
          }
 
          .mock-card {
            padding: 0.8rem;
          }
 
          .mock-card:nth-child(3) {
            grid-column: span 2;
          }
 
          .mock-card-value {
            font-size: 1.4rem;
          }
 
          .mock-card-label {
            font-size: 0.6rem;
          }
 
          .mock-bar-section {
            margin-top: 0.8rem;
          }
 
          .mock-bar-label {
            font-size: 0.65rem;
          }
 
          #features, #pricing {
            padding: 4rem 1.5rem;
          }
 
          .section-title {
            font-size: 1.8rem;
          }
 
          .section-sub {
            font-size: 0.95rem;
            margin-bottom: 2rem;
          }
 
          #features .features-grid {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
 
          #features .feature-card {
            padding: 1.5rem;
          }
 
          #features .feature-card.big {
            grid-column: span 1 !important;
            flex-direction: column !important;
          }
 
          .feature-card.big .feature-visual {
            margin-top: 1rem;
          }
 
          .feature-title {
            font-size: 1rem;
          }
 
          .feature-desc {
            font-size: 0.85rem;
          }
 
          #workflow {
            padding: 4rem 1.5rem !important;
          }
 
          .workflow-container {
            flex-direction: column !important;
            align-items: center;
            gap: 1.5rem;
          }
 
          .workflow-step {
            min-width: auto;
            width: 100%;
            max-width: 280px;
          }
 
          .workflow-arrow {
            transform: rotate(90deg);
            margin: 0;
            padding: 0;
          }
 
          .workflow-icon {
            width: 60px;
            height: 60px;
            font-size: 1.5rem;
          }
 
          .workflow-desc {
            max-width: none;
            font-size: 0.85rem;
          }
 
          .workflow-publish {
            padding: 1.2rem;
          }
 
          .workflow-publish-inner {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }
 
          .workflow-publish-inner > div:last-child {
            margin-left: 0;
          }
 
          .billing-toggle {
            flex-direction: row;
            padding: 3px;
          }
 
          .toggle-option {
            padding: 0.6rem 1rem;
            font-size: 0.8rem;
          }
 
          .save-badge {
            font-size: 0.55rem;
            padding: 0.15rem 0.4rem;
          }
 
          .pricing-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem;
          }
 
          .pricing-card {
            padding: 2rem 1.5rem;
          }
 
          .price-amount {
            font-size: 2.8rem;
          }
 
          .plan-features li {
            font-size: 0.85rem;
          }
 
          .testimonials-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem;
          }
 
          .testi-card {
            padding: 1.5rem;
          }
 
          #testimonials, #faq {
            padding: 4rem 1.5rem !important;
          }
 
          .faq-q {
            padding: 1rem 1.2rem;
            font-size: 0.9rem;
          }
 
          .faq-a {
            font-size: 0.85rem;
          }
 
          .divider {
            margin: 0 1.5rem;
          }
 
          .landing-footer {
            padding: 3rem 1.5rem;
          }
        }
 
        @media (max-width: 480px) {
          .landing-page h1 {
            font-size: 2rem;
          }
 
          .badge {
            font-size: 0.7rem;
            padding: 0.3rem 0.8rem;
          }
 
          .mock-grid {
            grid-template-columns: 1fr !important;
          }
 
          .mock-card:nth-child(3) {
            grid-column: span 1;
          }
 
          .section-title {
            font-size: 1.5rem;
          }
 
          .workflow-icon {
            width: 50px;
            height: 50px;
            font-size: 1.3rem;
          }
 
          .price-amount {
            font-size: 2.4rem;
          }
        }
      `}</style>
 
      <div className="landing-page">
        <canvas id="aurora"></canvas>
 
        {/* NAV */}
        <nav className="landing-nav">
          <div className="logo">CreatorFlow</div>
          <ul className="nav-links">
            <li><a href="#features" onClick={(e) => handleSmoothScroll(e, '#features')}>Features</a></li>
            <li><a href="#workflow" onClick={(e) => handleSmoothScroll(e, '#workflow')}>How It Works</a></li>
            <li><a href="#pricing" onClick={(e) => handleSmoothScroll(e, '#pricing')}>Pricing</a></li>
          </ul>
          <button className="nav-cta" onClick={handleGetStarted}>Get Started Free</button>
          <div className="hamburger" id="hamburger" onClick={toggleMobileMenu}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </nav>
 
        {/* MOBILE MENU */}
        <div className="mobile-menu" id="mobileMenu">
          <a href="#features" onClick={(e) => { handleSmoothScroll(e, '#features'); closeMobileMenu(); }}>Features</a>
          <a href="#workflow" onClick={(e) => { handleSmoothScroll(e, '#workflow'); closeMobileMenu(); }}>How It Works</a>
          <a href="#pricing" onClick={(e) => { handleSmoothScroll(e, '#pricing'); closeMobileMenu(); }}>Pricing</a>
          <a href="/login" className="mobile-cta" onClick={handleGetStarted}>Get Started Free</a>
        </div>
 
        {/* HERO */}
        <section id="hero">
          <canvas id="plexus"></canvas>
          <div className="badge">
            <div className="badge-dot"></div>
            AI-Powered Content Creation
          </div>
 
          <h1>
            Your Content.<br />
            <span className="gradient-text">On Autopilot.</span>
          </h1>
 
          <p className="hero-sub">
            Stop staring at blank pages. CreatorFlow turns your ideas into
            <span> scroll-stopping content</span> across every platform — in seconds.
          </p>
 
          <div className="hero-actions">
            <a href="/login" className="btn-primary" onClick={handleGetStarted}>Start Creating Free →</a>
            <a href="#workflow" className="btn-ghost" onClick={(e) => handleSmoothScroll(e, '#workflow')}>See how it works</a>
          </div>
 
          <div className="hero-visual">
            <div className="dashboard-mock">
              <div className="mock-header">
                <div className="mock-dot"></div>
                <div className="mock-dot"></div>
                <div className="mock-dot"></div>
              </div>
              <div className="mock-grid" className="mock-grid-inner">
                <div className="mock-card">
                  <div className="mock-card-label">Posts Generated</div>
                  <div className="mock-card-value c1">2,847</div>
                </div>
                <div className="mock-card">
                  <div className="mock-card-label">Time Saved</div>
                  <div className="mock-card-value c2">94hrs</div>
                </div>
                <div className="mock-card" className="mock-card-full">
                  <div className="mock-card-label">Engagement ↑</div>
                  <div className="mock-card-value c3">3.2x</div>
                </div>
              </div>
              <div className="mock-bar-section">
                <div className="mock-bar-label"><span>Instagram Reels</span><span style={{color:'var(--cyan)'}}>87%</span></div>
                <div className="mock-bar"><div className="mock-bar-fill" style={{width:'87%',background:'linear-gradient(90deg,var(--cyan),var(--purple2))'}}></div></div>
                <div className="mock-bar-label"><span>YouTube Shorts</span><span style={{color:'var(--magenta)'}}>72%</span></div>
                <div className="mock-bar"><div className="mock-bar-fill" style={{width:'72%',background:'linear-gradient(90deg,var(--purple2),var(--magenta))'}}></div></div>
                <div className="mock-bar-label"><span>Facebook Reels</span><span style={{color:'var(--purple2)'}}>61%</span></div>
                <div className="mock-bar"><div className="mock-bar-fill" style={{width:'61%',background:'linear-gradient(90deg,var(--magenta),var(--purple))'}}></div></div>
              </div>
            </div>
          </div>
        </section>
 
        <div className="divider"></div>
 
        {/* FEATURES */}
        <section id="features">
          <p className="section-label reveal">What CreatorFlow Does</p>
          <h2 className="section-title reveal">Everything a creator needs.<br /><span className="gradient-text">Nothing they don't.</span></h2>
          <p className="section-sub reveal">One platform. Every format. Zero creative block.</p>
 
          <div className="features-grid">
            <div className="feature-card big reveal">
              <div className="feature-content">
                <div className="feature-icon fi-cyan">💡</div>
                <div className="feature-title">Idea Generator</div>
                <div className="feature-desc">Pick your platform, niche, target audience, and goal. Get tagged content ideas with one-click "Create Script" and "Generate Hooks" built right in.</div>
              </div>
              <div className="feature-visual">
                <div style={{display:'flex',flexDirection:'column',gap:'0.6rem'}}>
                  <div style={{background:'rgba(0,229,255,0.08)',border:'1px solid rgba(0,229,255,0.2)',borderRadius:'8px',padding:'0.7rem 0.9rem',fontSize:'0.78rem'}}>
                    <div style={{display:'flex',gap:'0.4rem',marginBottom:'0.4rem'}}>
                      <span style={{background:'rgba(0,229,255,0.15)',color:'var(--cyan)',padding:'0.15rem 0.5rem',borderRadius:'100px',fontSize:'0.65rem',fontWeight:600}}>TikTok</span>
                      <span style={{background:'rgba(168,85,247,0.15)',color:'var(--purple2)',padding:'0.15rem 0.5rem',borderRadius:'100px',fontSize:'0.65rem',fontWeight:600}}>Educational</span>
                    </div>
                    <div style={{color:'var(--white)',fontSize:'0.78rem',lineHeight:1.4}}>"Top 5 Hidden Study Spots in Dubai"</div>
                  </div>
                </div>
              </div>
            </div>
 
            <div className="feature-card reveal">
              <div className="feature-icon fi-magenta">✍️</div>
              <div className="feature-title">Writing Tools</div>
              <div className="feature-desc">Captions, hooks, CTAs, and threads written in your voice. Pick tone, platform, and goal — get copy optimized for every algorithm.</div>
            </div>
 
            <div className="feature-card reveal">
              <div className="feature-icon fi-purple">🎬</div>
              <div className="feature-title">Script Builder</div>
              <div className="feature-desc">Drop a topic and script style — get a full structured script with Hook, Beats, and Call to Action.</div>
            </div>
 
            <div className="feature-card reveal">
              <div className="feature-icon fi-cyan">🎙️</div>
              <div className="feature-title">AI Voiceovers</div>
              <div className="feature-desc">Turn any script into a natural-sounding voiceover. Pick voice, tone, and speed — ready for CapCut or Premiere.</div>
            </div>
 
            <div className="feature-card reveal">
              <div className="feature-icon fi-purple">✂️</div>
              <div className="feature-title">Editor</div>
              <div className="feature-desc">Transforms your script into a full editing plan — Auto Cut Map, scene breakdown, shot types, and Creator Tips.</div>
            </div>
 
            <div className="feature-card reveal">
              <div className="feature-icon fi-magenta">📊</div>
              <div className="feature-title">Content Analyzer</div>
              <div className="feature-desc">Scores your content on Hook Strength, SEO, Click-Through, Readability, and Overall. Rewrites your title and description.</div>
            </div>
            <div className="feature-card big reveal" style={{display:'flex',alignItems:'center',gap:'2rem'}}>
              <div className="feature-content">
                <div className="feature-icon fi-cyan">📅</div>
                <div className="feature-title">30-Day Planner</div>
                <div className="feature-desc">Plan a full month of content in minutes. Auto-schedule across Instagram, YouTube, and Facebook Reels with one click. Exclusive to Creator Max.</div>
              </div>
              <div className="feature-visual" style={{display:'flex',flexDirection:'column',gap:'0.6rem',minWidth:'180px'}}>
                {[
                  {color:'#a855f7', label:'Instagram Reels'},
                  {color:'#ef4444', label:'YouTube Shorts'},
                  {color:'#3b82f6', label:'Facebook Reels'},
                ].map((p, i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'rgba(255,255,255,0.04)',borderRadius:'8px',padding:'8px 12px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                      <div style={{width:'8px',height:'8px',borderRadius:'50%',background:p.color}}></div>
                      <span style={{fontSize:'12px',color:'rgba(255,255,255,0.7)'}}>{p.label}</span>
                    </div>
                    <span style={{fontSize:'11px',color:'var(--cyan)',fontWeight:600}}>✓ Scheduled</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
 
        <div className="divider"></div>
 
        {/* WORKFLOW */}
        <section id="workflow" style={{position:'relative',zIndex:2,padding:'8rem 4rem',maxWidth:'1200px',margin:'0 auto'}}>
          <p className="section-label reveal">How It Works</p>
          <h2 className="section-title reveal">From idea to published.<br /><span className="gradient-text">In minutes, not hours.</span></h2>
          <p className="section-sub reveal">Five steps. Zero creative block. One platform.</p>
 
          <div className="workflow-container reveal" >
            <div className="workflow-step" >
              <div className="workflow-icon" style={{background:'linear-gradient(135deg,rgba(0,229,255,0.15),rgba(0,229,255,0.05))',borderColor:'rgba(0,229,255,0.3)'}}>💡</div>
              <div className="workflow-num" style={{color:'var(--cyan)'}}>01</div>
              <div className="workflow-title">Idea</div>
              <div className="workflow-desc">Drop your niche or topic. AI generates scroll-stopping content ideas.</div>
            </div>
            <div className="workflow-arrow" className="workflow-arrow-el">→</div>
            <div className="workflow-step" >
              <div className="workflow-icon" style={{background:'linear-gradient(135deg,rgba(168,85,247,0.15),rgba(168,85,247,0.05))',borderColor:'rgba(168,85,247,0.3)'}}>🪝</div>
              <div className="workflow-num" style={{color:'var(--purple2)'}}>02</div>
              <div className="workflow-title">Hook</div>
              <div className="workflow-desc">Turn your idea into a killer opening line that stops the scroll.</div>
            </div>
            <div className="workflow-arrow" className="workflow-arrow-el">→</div>
            <div className="workflow-step" >
              <div className="workflow-icon" style={{background:'linear-gradient(135deg,rgba(224,64,251,0.15),rgba(224,64,251,0.05))',borderColor:'rgba(224,64,251,0.3)'}}>🎬</div>
              <div className="workflow-num" style={{color:'var(--magenta)'}}>03</div>
              <div className="workflow-title">Script</div>
              <div className="workflow-desc">Full video script built around your hook. Paced for any platform.</div>
            </div>
            <div className="workflow-arrow" className="workflow-arrow-el">→</div>
            <div className="workflow-step" >
              <div className="workflow-icon" style={{background:'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(124,58,237,0.05))',borderColor:'rgba(124,58,237,0.3)'}}>🎙️</div>
              <div className="workflow-num" style={{color:'var(--purple)'}}>04</div>
              <div className="workflow-title">Voiceover</div>
              <div className="workflow-desc">Your script becomes a natural AI voiceover in one click.</div>
            </div>
            <div className="workflow-arrow" className="workflow-arrow-el">→</div>
            <div className="workflow-step" >
              <div className="workflow-icon" style={{background:'linear-gradient(135deg,rgba(0,229,255,0.12),rgba(168,85,247,0.08))',borderColor:'rgba(0,229,255,0.25)'}}>✂️</div>
              <div className="workflow-num" style={{background:'linear-gradient(135deg,var(--cyan),var(--purple2))',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>05</div>
              <div className="workflow-title">Editor</div>
              <div className="workflow-desc">Get a full editing plan with Auto Cut Map and Creator Tips.</div>
            </div>
          </div>
 
          <div className="workflow-publish reveal">
            <div className="workflow-publish-inner" >
              <span style={{fontSize:'1.5rem'}}>🚀</span>
              <div>
                <div style={{fontFamily:"'Cabinet Grotesk','Syne',sans-serif",fontWeight:800,fontSize:'1.1rem'}}>Publish Everywhere</div>
                <div style={{color:'var(--muted)',fontSize:'0.85rem',marginTop:'0.2rem'}}>Instagram · YouTube · Facebook · TikTok</div>
              </div>
              <div className="platform-tags">
                <span className="platform-tag" style={{background:'rgba(224,64,251,0.1)',borderColor:'rgba(224,64,251,0.3)',color:'var(--magenta)'}}>Instagram</span>
                <span className="platform-tag" style={{background:'rgba(0,229,255,0.1)',borderColor:'rgba(0,229,255,0.3)',color:'var(--cyan)'}}>YouTube</span>
                <span className="platform-tag" style={{background:'rgba(168,85,247,0.1)',borderColor:'rgba(168,85,247,0.3)',color:'var(--purple2)'}}>Facebook</span>
              </div>
            </div>
          </div>
        </section>
 
        <div className="divider"></div>
 
        {/* PRICING */}
        <section id="pricing">
          <p className="section-label reveal">Simple Pricing</p>
          <h2 className="section-title reveal">Pick your plan.<br /><span className="gradient-text">Start creating today.</span></h2>
          <p className="section-sub reveal">No hidden fees. Cancel anytime.</p>
 
          <div className="billing-toggle reveal" id="billingToggle">
            <button className="toggle-option active" data-billing="monthly" onClick={() => setBilling('monthly')}>Monthly</button>
            <button className="toggle-option" data-billing="yearly" onClick={() => setBilling('yearly')}>
              Yearly
              <span className="save-badge">Save 20%</span>
            </button>
          </div>
 
          <div className="pricing-grid" >
            <div className="pricing-card reveal" data-plan="beginner">
              <div className="plan-name">Beginner</div>
              <div className="plan-price">
                <div className="price-amount" data-monthly="$9" data-yearly="$7">$9</div>
                <div className="price-period">/mo</div>
              </div>
              <p className="plan-desc plan-desc-default">Get started creating.</p>
              <p className="plan-desc plan-desc-yearly" style={{display:'none'}}>Billed as $84/year</p>
              <ul className="plan-features">
                <li><div className="check">✓</div> 50 credits per month</li>
                <li><div className="check">✓</div> Idea Generator</li>
                <li><div className="check">✓</div> Script Builder</li>
                <li><div className="check">✓</div> Writing Tools</li>
              </ul>
              <button className="plan-btn outline" onClick={handleGetStarted}>Get Started</button>
            </div>
 
            <div className="pricing-card featured reveal" data-plan="pro">
              <div className="popular-badge">Most Popular</div>
              <div className="plan-name" style={{color:'var(--purple2)'}}>Pro Creator</div>
              <div className="plan-price">
                <div className="price-amount" style={{background:'linear-gradient(135deg,var(--purple),var(--purple2))',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}} data-monthly="$19" data-yearly="$15">$19</div>
                <div className="price-period">/mo</div>
              </div>
              <p className="plan-desc plan-desc-default">For serious creators.</p>
              <p className="plan-desc plan-desc-yearly" style={{display:'none'}}>Billed as $180/year</p>
              <ul className="plan-features">
                <li><div className="check">✓</div> 200 credits per month</li>
                <li><div className="check">✓</div> All Beginner features</li>
                <li><div className="check">✓</div> Editor</li>
                <li><div className="check">✓</div> AI Voiceovers</li>
                <li><div className="check">✓</div> Content Analyzer</li>
              </ul>
              <button className="plan-btn solid" onClick={handleGetStarted}>Get Pro Creator →</button>
            </div>
 
            <div className="pricing-card reveal" style={{borderColor:'rgba(0,229,255,0.3)',background:'rgba(0,229,255,0.04)'}} data-plan="max">
              <div className="plan-name" style={{color:'var(--cyan)'}}>✦ Creator Max</div>
              <div className="plan-price">
                <div className="price-amount" style={{background:'linear-gradient(135deg,var(--cyan),var(--cyan2))',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}} data-monthly="$49" data-yearly="$39">$49</div>
                <div className="price-period">/mo</div>
              </div>
              <p className="plan-desc plan-desc-default">For power creators.</p>
              <p className="plan-desc plan-desc-yearly" style={{display:'none'}}>Billed as $468/year</p>
              <ul className="plan-features">
                <li><div className="check" style={{borderColor:'rgba(0,229,255,0.4)',color:'var(--cyan)'}}>✓</div> 600 credits per month</li>
                <li><div className="check" style={{borderColor:'rgba(0,229,255,0.4)',color:'var(--cyan)'}}>✓</div> All Pro features</li>
                <li><div className="check" style={{borderColor:'rgba(0,229,255,0.4)',color:'var(--cyan)'}}>✓</div> Priority AI</li>
                <li><div className="check" style={{borderColor:'rgba(0,229,255,0.4)',color:'var(--cyan)'}}>✓</div> 30-Day Planner</li>
              </ul>
              <button className="plan-btn outline" style={{borderColor:'rgba(0,229,255,0.4)',color:'var(--cyan)'}} onClick={handleGetStarted}>Get Creator Max</button>
            </div>
          </div>
        </section>
 
        {/* FAQ */}
        <div className="divider"></div>
        <section id="faq" style={{position:'relative',zIndex:2,padding:'8rem 4rem',maxWidth:'800px',margin:'0 auto'}}>
          <p className="section-label reveal">FAQ</p>
          <h2 className="section-title reveal">Got questions?<br /><span className="gradient-text">We've got answers.</span></h2>
 
          <div className="faq-list" style={{marginTop:'3rem'}}>
            {[
              {q:'What are credits and how do they work?', a:'Credits are used for AI-powered features like Writing Tools and Content Analyzer. Basic features like the Idea Generator and Script Builder use minimal credits. Each plan comes with a monthly credit allowance that resets every billing cycle.'},
              {q:'Can I cancel my subscription anytime?', a:"Yes, absolutely. No contracts, no questions asked. Cancel anytime from your billing settings and you'll retain access until the end of your current billing period."},
              {q:'What platforms does CreatorFlow support?', a:"CreatorFlow is optimized for Instagram Reels, YouTube Shorts, Facebook Reels, and TikTok. Content is automatically formatted and paced for each platform's algorithm."},
              {q:'Is the content actually unique or will it sound like AI?', a:'CreatorFlow is trained to write in a natural, human tone — not the robotic AI style you might be used to. You can also use the Editor to adjust the tone, add your personality, and make it sound 100% like you before publishing.'},
              {q:"What's the difference between Pro Creator and Creator Max?", a:'Pro Creator gives you 200 credits and access to all core tools — Idea Generator, Script Builder, Writing Tools, Editor, AI Voiceovers, and Content Analyzer (15 credits per analysis). Creator Max gives you 600 credits, priority AI processing, exclusive templates, and the 30-Day Content Planner.'},
              {q:'Do unused credits roll over to next month?', a:'Credits reset at the start of each billing cycle and do not roll over. We recommend choosing a plan that matches your typical monthly usage to get the most value.'},
            ].map((item, i) => (
              <div key={i} className={`faq-item${openFaq === i ? ' open' : ''}`} onClick={() => toggleFaq(i)}>
                <div className="faq-q">{item.q} <span className="faq-icon">{openFaq === i ? '×' : '+'}</span></div>
                <div className="faq-a">{item.a}</div>
              </div>
            ))}
          </div>
        </section>
 
        {/* FOOTER */}
        <footer className="landing-footer">
          <div className="logo">CreatorFlow</div>
          <p>© 2025 CreatorFlow. Built for creators, by creators.</p>
        </footer>
      </div>
    </>
  );
};
 
export default LandingPage;
